/**
 * Landing Page Brokers & Exchanges Section
 * ZO Bull Algo Trading Platform
 */
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

const CRYPTO_EXCHANGES = [
  { name: 'Binance', icon: '🟡', marketsKey: 'marketSpotFutures' },
  { name: 'Bybit', icon: '🟠', marketsKey: 'marketSpotFuturesOptions' },
  { name: 'OKX', icon: '⚫', marketsKey: 'marketSpotFuturesOptions' },
  { name: 'Deribit', icon: '🟣', marketsKey: 'marketOptions' },
  { name: 'Delta Exchange', icon: '🔵', marketsKey: 'marketFuturesOptions' },
]

const INDIAN_BROKERS = [
  { name: 'Zerodha', tierKey: 'tierPopular' },
  { name: 'Upstox', tierKey: 'tierPopular' },
  { name: 'Angel One', tierKey: 'tierPopular' },
  { name: 'Fyers', tierKey: 'tierPopular' },
  { name: 'ICICI Direct', tierKey: 'tierPremium' },
  { name: 'HDFC Securities', tierKey: 'tierPremium' },
  { name: 'Kotak Securities', tierKey: 'tierPremium' },
  { name: 'Groww', tierKey: 'tierPopular' },
  { name: '5Paisa', tierKey: 'tierStandard' },
  { name: 'Alice Blue', tierKey: 'tierStandard' },
  { name: 'Samco', tierKey: 'tierStandard' },
  { name: 'Motilal Oswal', tierKey: 'tierPremium' },
]

const STATS = {
  cryptoExchanges: 11,
  indianBrokers: 94,
  brokerAPIs: 82,
  segments: 5
}

export default function LandingBrokers() {
  const { t } = useTranslation();

  return (
    <section className="landing-brokers">
      <div className="section-container">
        <div className="section-header">
          <span className="section-badge">🌐 {t('landing.brokersTitle')}</span>
          <h2 className="section-title">{t('landing.brokersSubtitle')}</h2>
          <p className="section-subtitle">
            {t('landing.brokersDesc')}
          </p>
        </div>
        
        <div className="brokers-stats">
          <div className="broker-stat-card">
            <span className="broker-stat-icon">🌐</span>
            <div className="broker-stat-content">
              <span className="broker-stat-value">{STATS.cryptoExchanges}</span>
              <span className="broker-stat-label">{t('landing.cryptoExchanges')}</span>
            </div>
          </div>
          <div className="broker-stat-card">
            <span className="broker-stat-icon">🏦</span>
            <div className="broker-stat-content">
              <span className="broker-stat-value">{STATS.indianBrokers}</span>
              <span className="broker-stat-label">{t('landing.indianBrokerAPIs')}</span>
            </div>
          </div>
          <div className="broker-stat-card">
            <span className="broker-stat-icon">🔗</span>
            <div className="broker-stat-content">
              <span className="broker-stat-value">{STATS.brokerAPIs}</span>
              <span className="broker-stat-label">{t('landing.brokerCompanies')}</span>
            </div>
          </div>
          <div className="broker-stat-card">
            <span className="broker-stat-icon">📊</span>
            <div className="broker-stat-content">
              <span className="broker-stat-value">{STATS.segments}</span>
              <span className="broker-stat-label">{t('landing.tradingSegments')}</span>
            </div>
          </div>
        </div>
        
        <div className="brokers-content">
          <div className="brokers-section">
            <div className="brokers-section-header">
              <h3>₿ {t('landing.cryptoExchanges')}</h3>
              <span className="brokers-count">{CRYPTO_EXCHANGES.length}+ {t('landing.exchanges')}</span>
            </div>
            <div className="exchanges-grid">
              {CRYPTO_EXCHANGES.map((exchange, index) => (
                <div key={index} className="exchange-card">
                  <span className="exchange-icon">{exchange.icon}</span>
                  <div className="exchange-info">
                    <span className="exchange-name">{exchange.name}</span>
                    <span className="exchange-markets">{t(`landing.${exchange.marketsKey}`)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="brokers-section">
            <div className="brokers-section-header">
              <h3>🇮🇳 {t('landing.indianStockBrokers')}</h3>
              <span className="brokers-count">82 {t('landing.companies')}</span>
            </div>
            <div className="brokers-grid">
              {INDIAN_BROKERS.map((broker, index) => (
                <div key={index} className={`broker-card broker-tier-${broker.tierKey.replace('tier', '').toLowerCase()}`}>
                  <span className="broker-name">{broker.name}</span>
                  <span className="broker-tier-badge">{t(`landing.${broker.tierKey}`)}</span>
                </div>
              ))}
              <div className="broker-card broker-more">
                <span className="broker-name">+72 {t('landing.more')}</span>
                <span className="broker-tier-badge">{t('landing.brokers')}</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="brokers-segments">
          <h4>{t('landing.supportedSegments')}</h4>
          <div className="segments-grid">
            <div className="segment-card">
              <span className="segment-icon">📈</span>
              <span className="segment-name">{t('landing.indianEquity')}</span>
              <span className="segment-exchanges">NSE, BSE</span>
            </div>
            <div className="segment-card">
              <span className="segment-icon">📊</span>
              <span className="segment-name">{t('landing.indianOptions')}</span>
              <span className="segment-exchanges">NSE, BSE</span>
            </div>
            <div className="segment-card">
              <span className="segment-icon">₿</span>
              <span className="segment-name">{t('landing.cryptoSpot')}</span>
              <span className="segment-exchanges">{t('landing.allExchanges')}</span>
            </div>
            <div className="segment-card">
              <span className="segment-icon">🔮</span>
              <span className="segment-name">{t('landing.cryptoFutures')}</span>
              <span className="segment-exchanges">Binance, Bybit, OKX</span>
            </div>
            <div className="segment-card">
              <span className="segment-icon">⚡</span>
              <span className="segment-name">{t('landing.cryptoOptions')}</span>
              <span className="segment-exchanges">Deribit, OKX</span>
            </div>
          </div>
        </div>
        
        <div className="brokers-cta">
          <Link to="/login" className="btn btn-primary btn-lg">
            {t('landing.connectBroker')}
          </Link>
        </div>
      </div>
    </section>
  )
}
